/**
 * The owners' screen.
 *
 * Three jobs, in order of how often they matter:
 *
 * 1. **Tell a customer from a friend.** Everything here separates paying subscribers
 *    from gifted access, because `plan = 'pro'` covers both and the number that
 *    validates the business only counts the first. Twenty friends is not twenty
 *    subscribers, and a dashboard that cannot say so will let you believe it is.
 * 2. **Give and take back a seat.** Friends-and-family access is granted here, by an
 *    admin, against a named person — there is deliberately no redeemable code for it.
 * 3. **See whether the chart library is actually covering anything.**
 *
 * Every endpoint behind this is gated by `requireAdmin` in the Worker. The route
 * hiding itself from non-admins is a courtesy so the nav is not littered with a link
 * that 403s; it is not the access control.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { api, ApiError, money, type AdminMetrics, type AdminUser,
         type PayoutLedger, type PayoutPerson } from "../lib/api";
import { useMeContext } from "../lib/MeContext";
import { Card, Notice, Skeleton } from "../components/ui";

const day = (ts: number | null) =>
  ts ? new Date(ts * 1000).toISOString().slice(0, 10) : "—";

/** What is granting this person access, in words rather than a product id. */
function grantLabel(u: AdminUser): { text: string; tone: string } {
  if (u.deleted_at) return { text: "deleted", tone: "g-dead" };
  switch (u.grant_product) {
    case "pro_annual": return { text: "paying · annual", tone: "g-paid" };
    case "pro_monthly": return { text: "paying · monthly", tone: "g-paid" };
    case "gift": return { text: "gift · for life", tone: "g-gift" };
    case "comp": return { text: "comp", tone: "g-gift" };
    default: return { text: u.plan === "pro" ? "pro (no entitlement?)" : "free", tone: "g-free" };
  }
}

export function Admin() {
  const { getToken } = useAuth();
  const { me } = useMeContext();
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [payouts, setPayouts] = useState<PayoutLedger | null>(null);
  /* The planning assumptions the projection runs against. Inputs, not guesses. */
  const [prize, setPrize] = useState(100);
  const [winners, setWinners] = useState(1);
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  /**
   * `getToken` is a fresh closure on every render, so depending on it here fired the
   * effect on every render — and because the effect sets state, that is a fetch loop
   * that never settles. Held in a ref, with the effect depending on nothing. Same
   * remedy as Play.tsx and useMe.ts, for the same reason.
   */
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;

  /*
    The assumptions travel in a ref for the same reason the token does: `load` must
    not be rebuilt every time a number input changes, or the effect that calls it
    refires on every keystroke.
  */
  const prizeRef = useRef(prize); prizeRef.current = prize;
  const winnersRef = useRef(winners); winnersRef.current = winners;

  const load = useCallback(async () => {
    try {
      const [m, u, p] = await Promise.all([
        api.adminMetrics(tokenRef.current),
        api.adminUsers(tokenRef.current),
        /*
          The ledger must not take the page down with it. It reads tables that are
          empty until the first tournament runs, and an admin screen that shows
          nothing at all because the prize ledger is quiet is worse than one that
          shows the business numbers and says the ledger could not be read.
        */
        api.adminPayouts(tokenRef.current, { prize: prizeRef.current, winners: winnersRef.current })
          .catch(() => null),
      ]);
      setMetrics(m);
      setUsers(u.users);
      setPayouts(p);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.humane : "Could not load the admin data.");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (me && me.user.role !== "admin") {
    return (
      <Card>
        <h1>Admin</h1>
        <p className="muted">This page is for account owners.</p>
      </Card>
    );
  }

  async function act(userId: string, what: "gift" | "revoke") {
    setBusy(userId);
    setNote(null);
    try {
      if (what === "gift") {
        await api.adminComp(getToken, userId, { forever: true });
        setNote("Gift seat granted — free for life, no card, nothing to cancel.");
      } else {
        const r = await api.adminRevoke(getToken, userId);
        setNote(`Gifted access removed. That account is now ${r.plan}.`);
      }
      await load();
    } catch (e) {
      setNote(
        e instanceof ApiError && e.body && typeof e.body === "object" &&
        "error" in e.body && e.body.error === "gift_seats_exhausted"
          ? "No gift seats left. Revoke one first, or raise GIFT_SEATS.max in config/plans.ts."
          : e instanceof ApiError ? e.humane : "That didn't work.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page">
      <h1>Admin</h1>

      {error && <Notice kind="err">{error}</Notice>}
      {note && <Notice kind="ok">{note}</Notice>}

      {/* ------------------------------------------------------- metrics --- */}
      {!metrics ? <Card><Skeleton lines={3} /></Card> : (
        <Card>
          <h2>Where the business actually is</h2>
          <div className="admin-stats">
            <Stat label="Paying" value={metrics.paying} hint="bought a subscription" big />
            <Stat label="Trialing" value={metrics.trialing} hint="card in, not charged yet" />
            <Stat label="Gifted" value={metrics.gifted} hint="given access, not customers" />
            <Stat label="Accounts" value={metrics.users} hint="signed up" />
            <Stat label="Spots, 7d" value={metrics.spots7d} hint="trainer usage" />
            <Stat
              label="Trial → paid"
              value={metrics.trialToPaid === null ? "—" : `${Math.round(metrics.trialToPaid * 100)}%`}
              hint="over people with a card"
            />
          </div>
          <p className="muted small">
            <strong>Paying</strong> is the proof-of-concept number — twenty of those is
            the target. Gifted access grants exactly the same product and counts for
            nothing here on purpose: friends and family are not evidence that strangers
            will pay. Gift seats used: {metrics.giftSeatsUsed} of {metrics.giftSeatsMax}.
          </p>
        </Card>
      )}

      {/* ------------------------------------------------------- payouts --- */}
      {payouts && <Payouts
        data={payouts}
        prize={prize} winners={winners}
        onPrize={setPrize} onWinners={setWinners}
        onRerun={() => { void load(); }}
      />}

      {/* --------------------------------------------------------- users --- */}
      <Card>
        <h2>Accounts</h2>
        <p className="muted small">
          A gift seat is free for life: no card, no Stripe subscription, nothing to
          cancel or refund. It can be taken back here, and taking it back only ever
          touches gifted access — a real subscription is cancelled in Stripe, not from
          this screen.
        </p>

        {!users ? <Skeleton lines={5} /> : users.length === 0 ? (
          <p className="muted small">No accounts yet.</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th><th>Access</th><th>Status</th>
                  <th>Expires</th><th>Joined</th><th />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const g = grantLabel(u);
                  const gifted = u.grant_product === "gift" || u.grant_product === "comp";
                  const paying = u.grant_product === "pro_monthly" || u.grant_product === "pro_annual";
                  return (
                    <tr key={u.id}>
                      <td className="admin-email">
                        {u.email}
                        {u.role === "admin" && <em className="admin-tag">owner</em>}
                      </td>
                      <td><span className={`grant ${g.tone}`}>{g.text}</span></td>
                      <td className="muted small">{u.plan_status ?? "—"}</td>
                      <td className="muted small num">
                        {u.grant_product === "gift" ? "never" : day(u.plan_expires_at)}
                      </td>
                      <td className="muted small num">{day(u.created_at)}</td>
                      <td className="admin-actions">
                        {!u.deleted_at && !gifted && (
                          <button
                            className="btn-quiet btn-tiny" disabled={busy === u.id}
                            onClick={() => void act(u.id, "gift")}
                            title={paying
                              ? "They are paying. A gift seat would not stop the charge — cancel in Stripe first."
                              : "Free for life. No card, nothing to cancel."}
                          >
                            {busy === u.id ? "…" : "Gift for life"}
                          </button>
                        )}
                        {gifted && (
                          <button
                            className="btn-quiet btn-tiny" disabled={busy === u.id}
                            onClick={() => void act(u.id, "revoke")}
                          >
                            {busy === u.id ? "…" : "Revoke"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/* --------------------------------------------------------------- payouts --- */

/**
 * Prize headroom: what has gone to whom, and how many more events that allows.
 *
 * The two projections are shown side by side and neither is presented as the answer,
 * because there isn't one — the same prize pool goes much further spread across a
 * field than concentrated on one person, and which of those is happening is a fact
 * about the past few months rather than something this screen can decide.
 *
 * Everything here is bookkeeping. Nothing in the product pays anyone, and whether an
 * award is reportable is a question for an accountant, which the payload says and
 * this card repeats.
 */
function Payouts({ data, prize, winners, onPrize, onWinners, onRerun }: {
  data: PayoutLedger;
  prize: number; winners: number;
  onPrize: (v: number) => void; onWinners: (v: number) => void;
  onRerun: () => void;
}) {
  const p = data.projection;
  const conc = Math.round(p.concentration * 100);

  return (
    <Card>
      <h2>Prize headroom · {data.year}</h2>
      <p className="muted small">
        Counted per person per calendar year against ${data.threshold}. Bookkeeping
        only — nothing here is a balance or anything owed.
      </p>

      <div className="admin-stats">
        <Stat label="Events, if one keeps winning" value={p.concentratedEvents}
              hint="bounded by whoever has least room" big />
        <Stat label="Events, if prizes spread" value={p.spreadEvents}
              hint="across everyone eligible" big />
        <Stat label="Room left" value={money(Math.round(p.totalHeadroom * 100))}
              hint="across everyone counted" />
        <Stat label="Concentration" value={`${conc}%`}
              hint={conc >= 60 ? "closer to the first number" : "closer to the second"} />
        <Stat label="At the limit" value={p.blocked} hint="cannot be given more this year" />
        <Stat label="Close to it" value={p.approaching} hint="inside the warning band" />
      </div>

      {/*
        The gap between the two numbers IS the finding, so it is said in words. A
        reader who takes only the larger one away has read the screen backwards.
      */}
      <p className="muted small">
        {p.spreadEvents > p.concentratedEvents ? (
          <>
            Those two numbers are the same prize money reaching different distances.
            If the same person keeps winning you get about <strong>{p.concentratedEvents}</strong>{" "}
            more {p.concentratedEvents === 1 ? "event" : "events"}; if prizes spread across the
            field, about <strong>{p.spreadEvents}</strong>. Concentration is running at{" "}
            {conc}%, so plan nearer the {conc >= 60 ? "first" : "second"}.
          </>
        ) : (
          <>Not enough handed out yet for the spread to make a difference.</>
        )}
      </p>

      <div className="payout-assume">
        <label>
          Prize per event
          <input type="number" min={0} step={5} value={prize}
                 onChange={(e) => onPrize(Math.max(0, Number(e.target.value) || 0))} />
        </label>
        <label>
          Winners per event
          <input type="number" min={1} step={1} value={winners}
                 onChange={(e) => onWinners(Math.max(1, Math.floor(Number(e.target.value) || 1)))} />
        </label>
        <button className="btn-quiet" onClick={onRerun}>Re-run</button>
      </div>

      {data.people.length === 0 ? (
        <p className="muted">Nothing awarded yet this year.</p>
      ) : (
        <table className="sessions payout-table">
          <thead>
            <tr><th>Who</th><th>Awards</th><th>Total</th><th>Room left</th><th /></tr>
          </thead>
          <tbody>
            {data.people.map((r) => <PayoutRow key={r.userId} r={r} />)}
          </tbody>
        </table>
      )}

      <p className="muted small">{data.note}</p>
    </Card>
  );
}

const STATUS_LABEL: Record<PayoutPerson["status"], string> = {
  clear: "",
  approaching: "close",
  at_limit: "at the limit",
  over: "over",
};

function PayoutRow({ r }: { r: PayoutPerson }) {
  return (
    <tr className={`payout-${r.status}`}>
      <td>{r.displayName ?? r.email ?? r.userId}</td>
      <td className="num">{r.awards}</td>
      <td className="num">{money(Math.round(r.total * 100))}</td>
      <td className="num">{money(Math.round(r.headroom * 100))}</td>
      <td className="payout-flag">{STATUS_LABEL[r.status]}</td>
    </tr>
  );
}

function Stat({ label, value, hint, big }: {
  label: string; value: number | string; hint: string; big?: boolean;
}) {
  return (
    <div className={big ? "admin-stat admin-stat-big" : "admin-stat"}>
      <span className="admin-stat-n num">{value}</span>
      <span className="admin-stat-l">{label}</span>
      <span className="admin-stat-h">{hint}</span>
    </div>
  );
}
